const es = {
  header: {
    badge: "Guía Oficial de BotMix",
    title: "Centro de Auditoría Técnica y Trazabilidad — Webhook Logs",
    summary:
      "La pantalla de Auditoría es la 'caja negra' del sistema de comunicaciones de BotMix. Registra de forma inmutable cada petición entrante y saliente (Webhooks, integraciones externas, eventos de Meta Cloud API, chatbots y APIs de terceros). Te permite auditar en segundos por qué un mensaje no llegó, verificar el contenido exacto (payload JSON) enviado por un cliente o sistema externo, y depurar fallas técnicas sin necesidad de acceder a la consola del servidor.",
  },
  sections: [
    {
      id: "controles",
      title: "1. Controles Superiores y Búsqueda",
      description:
        "Herramientas para localizar y refrescar peticiones registradas en el servidor.",
      items: [
        {
          id: "buscador",
          title: "Buscador en Tiempo Real",
          definition:
            "Campo de filtrado instantáneo que busca coincidencias sobre el nombre del contacto, número de teléfono, origen o fragmentos de la URL del webhook.",
          importance:
            "Permite aislar todas las peticiones vinculadas a un cliente específico simplemente ingresando su número de teléfono o prefijo internacional.",
        },
        {
          id: "btn_actualizar",
          title: "Botón de Actualización (Refrescar)",
          definition:
            "Fuerza la re-consulta inmediata a la base de datos para traer las peticiones más recientes procesadas en los últimos segundos.",
        },
      ],
    },
    {
      id: "columnas",
      title: "2. Columnas y Datos de la Tabla de Auditoría",
      description:
        "Significado y utilidad de cada campo registrado en los registros de llamadas web.",
      items: [
        {
          id: "col_id",
          title: "ID de Registro",
          definition:
            "Identificador único correlativo del log en la base de datos. Sirve de referencia directa para soporte técnico o seguimiento de incidencias.",
        },
        {
          id: "col_fecha",
          title: "Fecha y Hora del Suceso",
          definition:
            "Marca de tiempo exacta (Día, Mes, Año, Hora, Minuto y Segundo) en que el webhook impactó en los servidores de BotMix.",
        },
        {
          id: "col_contacto",
          title: "Nombre y Teléfono del Contacto",
          definition:
            "Identificación del destinatario o remitente involucrado en el evento de comunicación procesado por la plataforma.",
        },
        {
          id: "col_origen",
          title: "Origen de la Petición",
          definition:
            "Fuente que originó la llamada: Meta Cloud API (WhatsApp Oficial), Baileys (WhatsApp QR), FlowBuilder, N8N, Zapier, Make o Webhooks personalizados.",
        },
        {
          id: "col_metodo",
          title: "Método HTTP (POST, GET, PUT)",
          definition:
            "Verbo del protocolo web utilizado. Los envíos de mensajes y notificaciones de eventos generalmente operan bajo el método POST.",
        },
        {
          id: "col_url",
          title: "URL de Destino / Endpoint",
          definition:
            "Dirección exacta del servidor que recibió la petición. Revela si la solicitud se procesó localmente o fue reenviada a un servicio externo.",
        },
        {
          id: "col_status",
          title: "Código de Estado HTTP (Status Code)",
          definition:
            "Resultado devuelto por el servidor al procesar la solicitud: 200/201 indica éxito; 400 datos malformados; 401/403 permisos insuficientes; 404 recurso inexistente; 500 error del servidor.",
          alert:
            "Si observas códigos de la serie 500 o 400 continuos, significa que la API externa o el servidor receptor rechazó la carga útil del mensaje.",
        },
      ],
    },
    {
      id: "inspeccion",
      title: "3. Inspección Profunda de Payloads (Modal de Detalle)",
      description:
        "Visualización forense de la estructura JSON enviada o recibida.",
      items: [
        {
          id: "modal_payload",
          title: "Botón de Inspección Visual (Icono de Ojo)",
          definition:
            "Abre el visor modal que expone el cuerpo completo de la petición (Body / Payload) estructurado en formato JSON legible.",
          importance:
            "Es la herramienta definitiva de diagnóstico para comprobar exactamente qué variables, textos, IDs de mensaje o parámetros de pasarelas de pago se enviaron en cada transacción.",
        },
      ],
    },
    {
      id: "faq",
      title: "4. Preguntas Frecuentes y Diagnóstico (FAQ)",
      description:
        "Guía rápida de resolución de problemas habituales en auditoría.",
      items: [
        {
          id: "faq_mensaje_no_llega",
          title: "¿Qué hacer si un cliente dice que envió un mensaje pero no aparece en Inbox?",
          definition:
            "Ingresa a Auditoría y busca su número de teléfono. Si el webhook figura con estado 200, el mensaje ingresó y fue enrutado (posiblemente a un bot o cola específica). Si no figura ningún log, el mensaje no llegó desde WhatsApp hacia el servidor.",
        },
        {
          id: "faq_espacio",
          title: "¿Los registros de auditoría consumen espacio infinito?",
          definition:
            "El sistema mantiene una política de rotación y depuración programada para conservar el historial relevante sin sobrecargar el almacenamiento de la base de datos.",
        },
      ],
    },
  ],
};
const en = {
  "header": {
    "badge": "BotMix Official Guide",
    "title": "Technical Audit & Traceability Center — Webhook Logs",
    "summary": "The Audit view is the 'black box' of BotMix communications. It immutably logs every inbound and outbound request (Webhooks, external integrations, Meta Cloud API events, chatbots, and third-party APIs). Audit why a message failed, inspect raw JSON payloads, and debug issues without server console access."
  },
  "sections": [
    {
      "id": "controles",
      "title": "1. Top Controls and Search",
      "description": "Tools to locate and refresh server-registered requests.",
      "items": [
        {
          "id": "buscador",
          "title": "Real-Time Search",
          "definition": "Instant filter matching contact name, phone number, origin channel, or webhook URL snippet."
        },
        {
          "id": "btn_actualizar",
          "title": "Refresh Button",
          "definition": "Forces an immediate database query to retrieve requests processed in the last few seconds."
        }
      ]
    },
    {
      "id": "columnas",
      "title": "2. Audit Table Columns and Data",
      "description": "Meaning and utility of each logged webhook field.",
      "items": [
        {
          "id": "col_fecha",
          "title": "Date and Time (Timestamp)",
          "definition": "Exact ISO moment when the event hit the system."
        },
        {
          "id": "col_evento",
          "title": "Event Type",
          "definition": "Event classification (e.g., messages.upsert, status.update, contact.sync)."
        },
        {
          "id": "col_estado",
          "title": "HTTP Status Code",
          "definition": "Server response code: 200 OK (Success), 4xx (Client Error), or 5xx (Server Error)."
        },
        {
          "id": "col_payload",
          "title": "Payload Viewer (JSON)",
          "definition": "Modal button to inspect the full raw JSON data payload received or sent."
        }
      ]
    }
  ]
};
const pt = {
  "header": {
    "badge": "Guia Oficial da BotMix",
    "title": "Centro de Auditoria Técnica e Rastreabilidade — Logs de Webhooks",
    "summary": "A tela de Auditoria é a 'caixa preta' do sistema de comunicações da BotMix. Registra de forma imutável todas as requisições recebidas e enviadas (Webhooks, integrações externas, Meta Cloud API e chatbots). Permite depurar falhas sem acesso ao servidor."
  },
  "sections": [
    {
      "id": "controles",
      "title": "1. Controles e Busca",
      "description": "Ferramentas para localizar requisições registradas no servidor.",
      "items": [
        {
          "id": "buscador",
          "title": "Busca em Tempo Real",
          "definition": "Filtro rápido por nome do contato, telefone ou endpoint da requisição."
        },
        {
          "id": "btn_actualizar",
          "title": "Botão Atualizar",
          "definition": "Atualiza imediatamente a tabela com os eventos mais recentes."
        }
      ]
    },
    {
      "id": "columnas",
      "title": "2. Colunas e Dados de Auditoria",
      "description": "Detalhamento de cada campo registrado nos logs.",
      "items": [
        {
          "id": "col_fecha",
          "title": "Data e Hora",
          "definition": "Instante exato em que o webhook foi processado."
        },
        {
          "id": "col_evento",
          "title": "Tipo de Evento",
          "definition": "Identificador do evento de integração recebido."
        },
        {
          "id": "col_estado",
          "title": "Status HTTP",
          "definition": "Código de retorno retornado pelo servidor (200, 400, 500)."
        },
        {
          "id": "col_payload",
          "title": "Visualizador de Payload JSON",
          "definition": "Permite inspecionar o corpo integral da mensagem trafegada."
        }
      ]
    }
  ]
};
const fr = {
  "header": {
    "badge": "Guide Officiel BotMix",
    "title": "Centre d'Audit Technique et Traçabilité — Logs Webhooks",
    "summary": "L'écran d'audit est la 'boîte noire' des communications BotMix. Il enregistre de façon immuable chaque requête entrante et sortante (Webhooks, API Meta Cloud, intégrations externes). Inspectez les payloads JSON et dépannez les anomalies techniques sans accès serveur."
  },
  "sections": [
    {
      "id": "controles",
      "title": "1. Contrôles et Recherche",
      "description": "Outils de localisation des requêtes reçues.",
      "items": [
        {
          "id": "buscador",
          "title": "Recherche en Temps Réel",
          "definition": "Filtrage instantané par nom, numéro ou URL."
        },
        {
          "id": "btn_actualizar",
          "title": "Bouton Rafraîchir",
          "definition": "Actualise les événements traités récemment."
        }
      ]
    },
    {
      "id": "columnas",
      "title": "2. Colonnes et Données du Journal",
      "description": "Détail des informations enregistrées.",
      "items": [
        {
          "id": "col_fecha",
          "title": "Date et Heure",
          "definition": "Horodatage précis de réception."
        },
        {
          "id": "col_evento",
          "title": "Type d'Événement",
          "definition": "Nature de l'événement webhook."
        },
        {
          "id": "col_estado",
          "title": "Statut HTTP",
          "definition": "Code HTTP retourné (200, 4xx, 5xx)."
        },
        {
          "id": "col_payload",
          "title": "Visualiseur JSON",
          "definition": "Affichage complet du corps de la requête."
        }
      ]
    }
  ]
};
const ar = {
  "header": {
    "badge": "دليل BotMix الرسمي",
    "title": "مركز التدقيق الفني والتتبع — سجلات Webhook",
    "summary": "شاشة التدقيق هي الصندوق الأسود لنظام اتصالات BotMix. تسجل كل طلب وارد وصادر (Webhooks وتكاملات Meta Cloud API والبوتات الخارجية) وتتيح فحص حمولات JSON وتشخيص الأعطال بسهولة."
  },
  "sections": [
    {
      "id": "controles",
      "title": "1. عناصر التحكم والبحث",
      "description": "أدوات لتحديد وتحديث الطلبات المسجلة في الخادم.",
      "items": [
        {
          "id": "buscador",
          "title": "البحث الفوري",
          "definition": "تصفية فورية حسب اسم جهة الاتصال أو الهاتف أو رابط الـ Webhook."
        },
        {
          "id": "btn_actualizar",
          "title": "زر التحديث",
          "definition": "جلب أحدث الطلبات المعالجة فورًا."
        }
      ]
    },
    {
      "id": "columnas",
      "title": "2. أعمدة وبيانات جدول التدقيق",
      "description": "تفاصيل كل حقل مسجل في سجلات المكالمات.",
      "items": [
        {
          "id": "col_fecha",
          "title": "التاريخ والوقت",
          "definition": "التوقيت الدقيق لوصول الحدث إلى النظام."
        },
        {
          "id": "col_evento",
          "title": "نوع الحدث",
          "definition": "تصنيف الحدث الوارد عبر Webhook."
        },
        {
          "id": "col_estado",
          "title": "رمز استجابة HTTP",
          "definition": "كود الاستجابة (200 ناجح، 4xx خطأ عميل، 5xx خطأ خادم)."
        },
        {
          "id": "col_payload",
          "title": "عارض حمولة JSON",
          "definition": "نافذة لمعاينة نص البيانات الخام المرسلة أو المستلمة."
        }
      ]
    }
  ]
};

export const AUDIT_HELP_DATA = { es, en, pt, fr, ar };
